import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Switch } from '../components/ui/Switch';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BrandProfile } from '../components/ai/BrandProfile';
import { AiScheduledTasks } from '../components/ai/AiScheduledTasks';
import type { AiBrandProfileResponse, AiSettingsResponse } from '../types/ai';

interface Form {
  enabled: boolean;
  dailyRequestCap: string;
}

function toForm(s: AiSettingsResponse): Form {
  return { enabled: s.enabled, dailyRequestCap: String(s.dailyRequestCap) };
}

export function AiSettingsPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => apiFetch<AiSettingsResponse>('/ai/settings'),
  });
  const profile = useQuery({
    queryKey: ['ai-brand-profile'],
    queryFn: () => apiFetch<AiBrandProfileResponse>('/ai/brand-profile'),
  });
  const [form, setForm] = useState<Form | null>(null);

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (next: { enabled: boolean; dailyRequestCap: number }) =>
      apiFetch<AiSettingsResponse>('/ai/settings', { method: 'PUT', body: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });

  if (isLoading || !form || !data) return <div className="text-muted">Loading AI assistant…</div>;

  const cap = Number(form.dailyRequestCap);
  const capValid = Number.isInteger(cap) && cap >= 1 && cap <= 200;

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  function save() {
    if (!form) return;
    saveMutation.mutate({
      enabled: form.enabled,
      dailyRequestCap: cap,
    });
  }

  const { usage } = data;

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">AI assistant</h1>
        <MockBadge />
      </header>

      {!isAdmin ? (
        <Alert>
          You have read-only access. Contact an administrator to change the AI assistant.
        </Alert>
      ) : null}
      {!data.available ? (
        <Alert>
          The AI assistant is not available in this environment, so it cannot be used even when it
          is switched on.
        </Alert>
      ) : null}
      {saveMutation.isSuccess ? (
        <div role="status" className="text-success-text text-sm">
          Saved.
        </div>
      ) : null}
      {saveMutation.isError ? <Alert>Could not save the changes. Try again.</Alert> : null}

      <Card className="p-4 grid grid-cols-3 max-[860px]:grid-cols-1 gap-4" aria-label="Overview">
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted">Assistant</span>
          <span className="text-lg font-semibold">{data.enabled ? 'On' : 'Off'}</span>
          <span className="text-xs text-muted">Daily limit {data.dailyRequestCap} requests</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted">Used today</span>
          <span className="text-lg font-semibold">
            {data.usage.requests} of {data.dailyRequestCap}
          </span>
          <span className="text-xs text-muted">
            requests · about {data.usage.neurons.toLocaleString('en')} of{' '}
            {data.usage.neuronBudget.toLocaleString('en')} neurons
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted">Brand profile</span>
          <span className="text-lg font-semibold">
            {profile.data
              ? `${profile.data.progress.answered} of ${profile.data.progress.total}`
              : '—'}
          </span>
          <a href="#brand-profile" className="text-xs text-link hover:underline">
            Go to the brand profile
          </a>
        </div>
      </Card>

      <Card className="p-4 flex flex-col gap-3">
        <h2 className="text-sm font-semibold m-0">Switch</h2>
        <div className="flex items-center gap-3">
          <Switch
            checked={form.enabled}
            disabled={!isAdmin}
            label="Use the AI assistant"
            onChange={() => update('enabled', !form.enabled)}
          />
          <span className="text-sm">{form.enabled ? 'On' : 'Off'}</span>
        </div>
        <p className="m-0 text-xs text-muted">
          Off by default. The assistant only writes drafts and suggestions. It never sends,
          approves, schedules or switches anything on, and it never sees contact names, emails or
          orders. It uses Cloudflare&apos;s free AI allowance, so it costs nothing.
        </p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ai-cap">Requests per day (1 to 200)</Label>
          <Input
            id="ai-cap"
            type="number"
            min={1}
            max={200}
            value={form.dailyRequestCap}
            disabled={!isAdmin}
            aria-describedby="ai-cap-help"
            onChange={(e) => update('dailyRequestCap', e.target.value)}
          />
          <p id="ai-cap-help" className="m-0 text-xs text-muted">
            When the limit is used up, the assistant stops until 00:00 UTC. Nothing is charged. The
            free allowance is {usage.freeNeuronsPerDay.toLocaleString('en')} neurons a day for the
            whole account.
          </p>
          {!capValid ? (
            <p className="m-0 text-xs text-danger-text">Enter a whole number from 1 to 200.</p>
          ) : null}
        </div>
        {isAdmin ? (
          <Button
            disabled={!capValid || saveMutation.isPending}
            onClick={save}
            className="self-end max-sm:self-stretch"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        ) : null}
      </Card>

      <AiScheduledTasks />

      <BrandProfile />
    </>
  );
}
